import React, { useMemo } from 'react';
import { Box, Typography, Grid, IconButton, Tooltip, Chip, Stack } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import AttStatCard from '../LeaveAttendanceComps/AttStatCard';
import { balanceTone, balanceWord } from './PaymentLedger';
import { card, th, td, EmptyState } from '../uiKit';
import { PRIMARY } from '../../theme';
import { inr, fmtDate, parseApiDate } from '../../utils/format';

const GRID = '1.5fr 90px 1fr 1fr 1fr 1fr 130px';
const LEDGER = '110px 110px 1.2fr 1.6fr 1fr 1fr';
const num = (v) => Number(v) || 0;
const toDate = (v) => { const d = parseApiDate(v); return d ? fmtDate(d) : '—'; };
const keyOf = (v) => String(v || '').trim().toLowerCase();
const statusOf = (b) => (b < 0 ? 'due' : b > 0 ? 'advance' : 'settled');

const KIND = {
    Expense: { c: '#C2475A', bg: '#FDECEF' },
    Prepaid: { c: '#2F8F5F', bg: '#E8F6EE' },
    Postpaid: { c: '#3B82C4', bg: '#E8F1FA' },
};

// The payment history seen by client rather than by employee. A client is the shoot's project name (e.g. Grohair): its expenses are
// the shoot expenses logged against that project, and its payments are the Add Payment entries made for that shoot.
export default function ClientPaymentHistory({ data, q, from, to, stype, client, clientSel = 'all', toolbar, onOpen, onBack }) {
    const { clients, byKey } = useMemo(() => {
        const inRange = (d) => !((from && d < from) || (to && d > to));
        const rows = data.rows.filter((r) => keyOf(r.project) && inRange(r.date) && (stype === 'all' || r.shootType === stype));
        const typeShoots = new Set(data.rows.filter((r) => stype === 'all' || r.shootType === stype).map((r) => keyOf(r.project)));
        const pays = data.payments.filter((p) => keyOf(p.shoot) && inRange(p.date) && typeShoots.has(keyOf(p.shoot)));
        const map = new Map();
        const get = (name) => {
            const k = keyOf(name);
            if (!map.has(k)) map.set(k, { key: k, name: String(name).trim(), rows: [], pays: [] });
            return map.get(k);
        };
        rows.forEach((r) => get(r.project).rows.push(r));
        pays.forEach((p) => get(p.shoot).pays.push(p));
        const list = [...map.values()].map((c) => {
            const total = c.rows.reduce((n, r) => n + num(r.amount), 0);
            const prepaid = c.pays.filter((p) => p.type === 'Prepaid').reduce((n, p) => n + num(p.amount), 0);
            const postpaid = c.pays.filter((p) => p.type === 'Postpaid').reduce((n, p) => n + num(p.amount), 0);
            const balance = prepaid + postpaid - total;
            const dates = [...c.rows.map((r) => r.date), ...c.pays.map((p) => p.date)].filter(Boolean).sort();
            return { ...c, entries: c.rows.length, total, prepaid, postpaid, balance, last: dates[dates.length - 1] || '' };
        }).sort((a, b) => a.name.localeCompare(b.name));
        return { clients: list, byKey: new Map(list.map((c) => [c.key, c])) };
    }, [data.rows, data.payments, from, to, stype]);

    const statSize = { xs: 12, sm: 6, md: 3 };
    const cardsFor = (list) => {
        const total = list.reduce((n, c) => n + c.total, 0);
        const prepaid = list.reduce((n, c) => n + c.prepaid, 0);
        const postpaid = list.reduce((n, c) => n + c.postpaid, 0);
        return { total, prepaid, postpaid, balance: prepaid + postpaid - total };
    };
    const Cards = ({ c }) => (
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid size={statSize}><AttStatCard label="Total expenses" value={inr(c.total)} icon={PaymentsRoundedIcon} tone="purple" /></Grid>
            <Grid size={statSize}><AttStatCard label="Prepaid" value={inr(c.prepaid)} icon={AccountBalanceWalletRoundedIcon} tone="green" /></Grid>
            <Grid size={statSize}><AttStatCard label="Postpaid" value={inr(c.postpaid)} icon={PendingActionsRoundedIcon} tone="amber" /></Grid>
            <Grid size={statSize}><AttStatCard label={c.balance > 0 ? 'Advance' : 'Balance due'} value={inr(Math.abs(c.balance))} icon={HourglassBottomRoundedIcon} tone="rose" /></Grid>
        </Grid>
    );

    // ---- One client: every expense and payment, date by date, with the running balance.
    if (client) {
        const c = byKey.get(keyOf(client));
        const name = c?.name || client;
        const lines = [
            ...(c?.rows || []).map((r) => ({ id: `e${r.id}`, date: r.date, kind: 'Expense', who: (r.employees || []).join(', ') || '—', detail: [r.category, r.location, r.notes].filter(Boolean).join(' · ') || '—', amount: num(r.amount), sign: -1 })),
            ...(c?.pays || []).map((p) => ({ id: `p${p.id}`, date: p.date, kind: p.type || 'Prepaid', who: p.employeeName || p.employeeCode || '—', detail: p.notes || '—', amount: num(p.amount), sign: 1 })),
        ].sort((a, b) => String(a.date).localeCompare(String(b.date)) || String(a.id).localeCompare(String(b.id)));
        let run = 0;
        const ledger = lines.map((l) => { run += l.sign * l.amount; return { ...l, balance: run }; });
        return (
            <Box sx={{ px: { xs: 1.5, md: 2.5 }, pt: 2.5, pb: 3 }}>
                <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center', mb: 2 }}>
                    <Tooltip arrow title="Back to clients">
                        <IconButton onClick={onBack} sx={{ border: '1px solid #E6EAF1', borderRadius: '9px', color: '#475569' }}><ArrowBackRoundedIcon sx={{ fontSize: 19 }} /></IconButton>
                    </Tooltip>
                    <Box sx={{ width: 38, height: 38, borderRadius: '10px', bgcolor: '#EDE9FE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <BusinessRoundedIcon sx={{ color: PRIMARY, fontSize: 21 }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#0F172A' }}>{name}</Typography>
                        <Typography sx={{ fontSize: 12, color: '#98A0AE' }}>{c ? `${c.entries} expense${c.entries === 1 ? '' : 's'} · ${c.pays.length} payment${c.pays.length === 1 ? '' : 's'}` : 'No activity for this filter'}</Typography>
                    </Box>
                </Stack>
                <Cards c={c ? cardsFor([c]) : { total: 0, prepaid: 0, postpaid: 0, balance: 0 }} />
                <Box sx={{ ...card, overflow: 'hidden' }}>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{ minWidth: 820 }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: LEDGER, bgcolor: '#F7F6FD' }}>
                                {['DATE', 'TYPE', 'EMPLOYEE', 'DETAILS', 'AMOUNT', 'BALANCE'].map((h) => <Box key={h} sx={th}>{h}</Box>)}
                            </Box>
                            {ledger.length === 0 ? (
                                <EmptyState icon={BusinessRoundedIcon} title="Nothing recorded for this client" hint="Shoot expenses and payments made for this client appear here." />
                            ) : ledger.map((l) => {
                                const k = KIND[l.kind] || KIND.Prepaid;
                                return (
                                    <Box key={l.id} sx={{ display: 'grid', gridTemplateColumns: LEDGER, alignItems: 'center', borderBottom: '1px solid #EEF0F6' }}>
                                        <Box sx={{ ...td, borderBottom: 'none' }}><Typography sx={{ fontSize: 13 }}>{toDate(l.date)}</Typography></Box>
                                        <Box sx={{ ...td, borderBottom: 'none' }}><Chip label={l.kind} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 800, bgcolor: k.bg, color: k.c }} /></Box>
                                        <Box sx={{ ...td, borderBottom: 'none' }}><Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap title={l.who}>{l.who}</Typography></Box>
                                        <Box sx={{ ...td, borderBottom: 'none' }}><Typography sx={{ fontSize: 12.5, color: '#64748B' }} noWrap title={l.detail}>{l.detail}</Typography></Box>
                                        <Box sx={{ ...td, borderBottom: 'none' }}><Typography sx={{ fontSize: 13, fontWeight: 800, color: k.c }}>{l.sign < 0 ? '−' : '+'}{inr(l.amount)}</Typography></Box>
                                        <Box sx={{ ...td, borderBottom: 'none' }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 800, color: balanceTone(statusOf(l.balance)) }}>{inr(Math.abs(l.balance))}</Typography>
                                            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: balanceTone(statusOf(l.balance)) }}>{balanceWord(statusOf(l.balance))}</Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    }

    // ---- The client list.
    const needle = q.trim().toLowerCase();
    const shown = clients.filter((c) => (!needle || c.name.toLowerCase().includes(needle)) && (clientSel === 'all' || c.key === keyOf(clientSel)));
    return (
        <Box sx={{ px: { xs: 1.5, md: 2.5 }, pt: 2.5, pb: 3 }}>
            {toolbar}
            <Cards c={cardsFor(shown)} />
            <Box sx={{ ...card, overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ minWidth: 820 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: GRID, bgcolor: '#F7F6FD' }}>
                            {['CLIENT', 'ENTRIES', 'TOTAL', 'PREPAID', 'POSTPAID', 'BALANCE', 'LAST ACTIVITY'].map((h) => <Box key={h} sx={th}>{h}</Box>)}
                        </Box>
                        {shown.length === 0 ? (
                            <EmptyState icon={BusinessRoundedIcon} title="No client history yet" hint="Shoot expenses are grouped by their project (client). Add one on the Shoot tab to see it here." />
                        ) : shown.map((c) => (
                            <Box key={c.key} onClick={() => onOpen(c.name)} sx={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', cursor: 'pointer', borderBottom: '1px solid #EEF0F6', '&:hover': { bgcolor: '#FAF9FF' } }}>
                                <Box sx={{ ...td, borderBottom: 'none' }}><Typography sx={{ fontSize: 13, fontWeight: 700, color: PRIMARY }} noWrap>{c.name}</Typography></Box>
                                <Box sx={{ ...td, borderBottom: 'none' }}><Typography sx={{ fontSize: 13 }}>{c.entries}</Typography></Box>
                                <Box sx={{ ...td, borderBottom: 'none' }}><Typography sx={{ fontSize: 13, fontWeight: 800 }}>{inr(c.total)}</Typography></Box>
                                <Box sx={{ ...td, borderBottom: 'none' }}><Typography sx={{ fontSize: 13, fontWeight: 700, color: '#2F8F5F' }}>{inr(c.prepaid)}</Typography></Box>
                                <Box sx={{ ...td, borderBottom: 'none' }}><Typography sx={{ fontSize: 13, fontWeight: 700, color: '#3B82C4' }}>{inr(c.postpaid)}</Typography></Box>
                                <Box sx={{ ...td, borderBottom: 'none' }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: balanceTone(statusOf(c.balance)) }}>{inr(Math.abs(c.balance))}</Typography>
                                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: balanceTone(statusOf(c.balance)) }}>{balanceWord(statusOf(c.balance))}</Typography>
                                </Box>
                                <Box sx={{ ...td, borderBottom: 'none' }}><Typography sx={{ fontSize: 13, color: '#64748B' }}>{toDate(c.last)}</Typography></Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
